/**
 * Mikrotik router catalog — desktop + rack-mount.
 *
 * All entries verified against mikrotik.com official product pages.
 * Each entry carries `datasheetUrl` + `// SRC:` comment with the page
 * actually read. Fields not in the source are left undefined — never
 * guessed. RouterOS license level is recorded in tagline (level 4/5/6
 * differ in tunnel + queue limits).
 *
 * Mikrotik routers ALL have built-in switch ports — they qualify as
 * `isSwitchableDevice`, so cameras can terminate directly on their LAN
 * ports. Port capacity check uses `lanPorts`.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const MIKROTIK_ROUTERS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // SOHO / Small business
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/RB750Gr3 (search snippets + official
  //      product page) — hEX (RB750Gr3): 5× GbE, dual-core 880 MHz, 256 MB
  //      RAM, USB, microSD, PoE-in on port 1 (8-30 V passive), no PoE-out.
  //      RouterOS Level 4.
  {
    id: 'mikrotik-hex-rb750gr3',
    manufacturer: 'Mikrotik',
    model: 'hEX (RB750Gr3)',
    category: 'router',
    tagline:
      'hEX — 5× GbE budget router, dual-core 880 MHz, passive PoE-in, RouterOS L4',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/RB750Gr3',
    specs: {
      wanPorts: 1, // any of the 5 can be WAN — port 1 is the default
      lanPorts: 4, // remaining 4 act as switch
      lanPortSpeed: '1G',
      wifi: 'none',
      cellular: 'none',
      formFactor: 'compact-desktop',
      management: 'hybrid',
    },
  },

  // SRC: https://mikrotik.com/product/hap_ax3
  //      hAP ax³: 4× GbE + 1× 2.5 GbE + 1× GbE with PoE-out (15 W),
  //      Wi-Fi 6 dual-band AX1800 (574 + 1200 Mbps), passive PoE-in 18-28 V,
  //      quad-core IPQ-6010 1.8 GHz, 1 GB RAM, 38 W max / 15 W idle.
  {
    id: 'mikrotik-hap-ax3',
    manufacturer: 'Mikrotik',
    model: 'hAP ax³',
    category: 'router',
    tagline:
      'hAP ax³ — 5× GbE + 1× 2.5 GbE, Wi-Fi 6 AX1800, PoE-in/out, RouterOS L4',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/hap_ax3',
    specs: {
      wanPorts: 1,
      lanPorts: 5, // 4× GbE + 1× 2.5 GbE (and 1 with PoE-out)
      lanPortSpeed: '2.5G',
      poeOutPorts: 1,
      poeOutBudgetW: 15,
      poeOutStandard: 'passive',
      throughputGbps: 2.66, // 2,662 Mbps bridging
      wifi: 'wifi6',
      cellular: 'none',
      formFactor: 'desktop',
      powerConsumptionW: 38,
      management: 'hybrid',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Mid-range / prosumer
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/l009uigs_rm
  //      L009UiGS-RM: 8× GbE + 1× 2.5G SFP, PoE-in on port 1 (802.3af/at),
  //      passive PoE-out on port 8, IPQ-5018 dual-core 800 MHz, 512 MB RAM,
  //      3.43 Gbps bridging, 40 W max / 8 W idle. Rack kit fits 4 units in 1U.
  {
    id: 'mikrotik-l009uigs-rm',
    manufacturer: 'Mikrotik',
    model: 'L009UiGS-RM',
    category: 'router',
    tagline:
      'L009UiGS — 8× GbE + 2.5G SFP, PoE-in/out, 3.4 Gbps bridging, 1U rack-kit',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/l009uigs_rm',
    specs: {
      wanPorts: 1,
      lanPorts: 7,
      lanPortSpeed: '1G',
      poeOutPorts: 1,
      poeOutBudgetW: 15,
      poeOutStandard: 'passive',
      sfpPorts: 1, // 2.5G SFP
      throughputGbps: 3.43,
      wifi: 'none',
      cellular: 'none',
      formFactor: '1U', // rackmount kit (4 units per 1U), but ships as desktop too
      powerConsumptionW: 40,
      management: 'hybrid',
    },
  },

  // SRC: https://mikrotik.com/product/rb4011igs_rm
  //      RB4011iGS+RM: 10× GbE + 1× SFP+ 10 G, passive PoE-in 18-57 V,
  //      passive PoE-out on port 10 (600 mA limit), quad-core AL21400 ARM
  //      1.4 GHz, 1 GB RAM, 9.79 Gbps bridging, 33 W max / 18 W idle.
  //      1U rack ears included.
  {
    id: 'mikrotik-rb4011igs-rm',
    manufacturer: 'Mikrotik',
    model: 'RB4011iGS+RM',
    category: 'router',
    tagline:
      'RB4011iGS+ — 10× GbE + SFP+ 10G, 9.8 Gbps bridging, 1U rack, RouterOS L5',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/rb4011igs_rm',
    specs: {
      wanPorts: 1,
      lanPorts: 9,
      lanPortSpeed: '1G',
      poeOutPorts: 1,
      poeOutStandard: 'passive',
      sfpPorts: 1, // SFP+ 10G
      throughputGbps: 9.79,
      wifi: 'none',
      cellular: 'none',
      formFactor: '1U',
      powerConsumptionW: 33,
      management: 'hybrid',
    },
  },

  // SRC: https://mikrotik.com/product/rb5009ug_s_in
  //      RB5009UG+S+IN: 7× GbE + 1× 2.5 GbE + 1× SFP+ 10 G, PoE-in on
  //      port 1 (802.3af/at), no PoE-out, Marvell Armada 88F7040 quad-core
  //      ARMv8 1.4 GHz, 1 GB DDR4, 9.8 Gbps bridging, IPsec 1.4 Gbps,
  //      14 W base / 25 W max. Mounting kit: 4 units per 1U.
  {
    id: 'mikrotik-rb5009ug-s-in',
    manufacturer: 'Mikrotik',
    model: 'RB5009UG+S+IN',
    category: 'router',
    tagline:
      'RB5009 — 7× GbE + 2.5 GbE + SFP+ 10G, 9.8 Gbps, IPsec 1.4 Gbps, ARMv8',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/rb5009ug_s_in',
    specs: {
      wanPorts: 1,
      lanPorts: 7, // 7× GbE
      lanPortSpeed: '2.5G',
      sfpPorts: 1, // SFP+ 10G
      throughputGbps: 9.8,
      wifi: 'none',
      cellular: 'none',
      formFactor: '1U', // 4 units per 1U with mounting kit
      powerConsumptionW: 25,
      management: 'hybrid',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Enterprise / carrier-class
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/ccr2004_1g_12s_2xs
  //      CCR2004-1G-12S+2XS: 1× GbE + 12× SFP+ 10G + 2× SFP28 25G, AL32400
  //      quad-core ARM64 1.7 GHz, 4 GB ECC RAM, 38.5 Gbps bridging, IPsec
  //      3.4 Gbps single-tunnel, dual redundant 100-240 V AC PSU, dual fans,
  //      60 W max / 31 W idle, 443×224×44 mm 1U, op temp -20..+60 °C,
  //      RouterOS Level 6.
  {
    id: 'mikrotik-ccr2004-1g-12s-2xs',
    manufacturer: 'Mikrotik',
    model: 'CCR2004-1G-12S+2XS',
    category: 'router',
    tagline:
      'CCR2004 — 12× SFP+ 10G + 2× SFP28 25G, 38.5 Gbps, redundant AC, 1U L6',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/ccr2004_1g_12s_2xs',
    specs: {
      wanPorts: 1,
      lanPorts: 0, // no copper LAN beyond the single mgmt GbE
      lanPortSpeed: '1G',
      sfpPorts: 14, // 12× SFP+ + 2× SFP28
      throughputGbps: 38.5,
      wifi: 'none',
      cellular: 'none',
      formFactor: '1U',
      powerConsumptionW: 60,
      management: 'hybrid',
    },
  },
];
